import type { BenefitSelectionWithSiCoverage } from '@/features/quotes/types/group-life/coverage-schema.types';
import { isDailyBenefitCoverage } from '@/features/quotes/utils/group-life/daily-benefit';
import type { CoverageSelection } from '@/features/quotes/utils/group-life/coverage-resolver';
import { getDisplaySumInsuredMax } from '@/features/quotes/utils/group-life/coverage-display.util';
import {
  formatCurrencyBRL,
  formatCurrencyInput,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import {
  CoverageRowShell,
  CurrencyControl,
  SelectControl,
} from './coverage-row-shell';

type Props = {
  coverage: BenefitSelectionWithSiCoverage;
  selection: CoverageSelection | undefined;
  onChange: (next: CoverageSelection) => void;
  disabled?: boolean;
  error?: string;
  maxDailyRate?: number;
};

/**
 * Cobertura BENEFIT_SELECTION_WITH_SI: o corretor escolhe uma opção da lista
 * (select) E informa o capital (input R$) com base na opção escolhida. Os dois
 * controles ficam empilhados à direita da linha.
 */
export function BenefitSelectionWithSiRow({
  coverage,
  selection,
  onChange,
  disabled,
  error,
  maxDailyRate,
}: Props) {
  const sel =
    selection?.kind === 'BENEFIT_SELECTION_WITH_SI' ? selection : undefined;
  const enabled = sel?.enabled ?? false;
  const selectedChildCode = sel?.selectedChildCoverageCode ?? null;
  const capital = sel?.capital ?? null;
  const display =
    capital == null
      ? ''
      : formatCurrencyInput(String(Math.round(capital * 100)));

  const options = coverage.children.map(child => ({
    value: child.code,
    label: child.name,
  }));

  const emit = (patch: {
    enabled?: boolean;
    selectedChildCoverageCode?: string | null;
    capital?: number | null;
  }) =>
    onChange({
      kind: 'BENEFIT_SELECTION_WITH_SI',
      enabled: patch.enabled ?? enabled,
      selectedChildCoverageCode:
        patch.selectedChildCoverageCode !== undefined
          ? patch.selectedChildCoverageCode
          : selectedChildCode,
      capital: patch.capital !== undefined ? patch.capital : capital,
    });

  const bounds: string[] = [];
  const daily = isDailyBenefitCoverage(coverage.code);
  if (!daily && coverage.sumInsuredMin != null)
    bounds.push(`Min: ${formatCurrencyBRL(coverage.sumInsuredMin)}`);
  const displayMax = daily ? maxDailyRate : getDisplaySumInsuredMax(coverage);
  if (displayMax != null) {
    bounds.push(
      daily
        ? `Máximo permitido: ${formatCurrencyBRL(displayMax)}`
        : `Max: ${formatCurrencyBRL(displayMax)}`
    );
  }

  return (
    <CoverageRowShell
      name={coverage.name}
      description={coverage.description}
      badge={coverage.isRider ? 'Adicional' : undefined}
      caption={bounds.length ? bounds.join(' · ') : undefined}
      enabled={enabled}
      onToggle={next => emit({ enabled: next })}
      disabled={disabled}
      error={error}
      control={
        <div className='flex flex-col items-end gap-2'>
          <SelectControl
            value={selectedChildCode ?? ''}
            onChange={value =>
              emit({ selectedChildCoverageCode: value || null })
            }
            options={options}
            disabled={disabled || !enabled}
            placeholder='Selecione uma opção'
          />
          <div className='flex flex-col items-end gap-1'>
            {daily ? (
              <span className='text-xs text-muted-foreground'>
                Teto da diária (R$)
              </span>
            ) : null}
            <CurrencyControl
              value={display}
              onChange={raw => emit({ capital: parseCurrencyInput(raw) })}
              disabled={disabled || !enabled}
              invalid={!!error}
              placeholder='R$ 0,00'
            />
          </div>
        </div>
      }
    />
  );
}
